import React, { createContext, useContext, useState, useEffect } from 'react';
import { db } from '../config/firebase'; // Corretto il percorso di importazione
import { doc, collection, onSnapshot, setDoc, updateDoc, deleteDoc, getDocs } from 'firebase/firestore';
import { useAuth } from './AuthContext'; // Corretto il percorso di importazione
import Toast from '../components/Toast';

// Creazione del contesto
const CartContext = createContext();

// Hook personalizzato per usare il contesto
export function useCart() {
  return useContext(CartContext);
}

// Provider del carrello
export function CartProvider({ children }) {
  const [cartItems, setCartItems] = useState([]);
  const { currentUser } = useAuth(); // Ottieni l'utente corrente dal contesto di autenticazione
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState({ show: false, message: '', variant: 'danger' });

  // Listener in tempo reale per il carrello dell'utente in Firestore
  useEffect(() => {
    if (!currentUser) {
      setCartItems([]);
      setLoading(false);
      return; // Nessun utente autenticato, carrello vuoto
    }

    const userCartRef = collection(db, 'users', currentUser.uid, 'cart');

    const unsubscribe = onSnapshot(userCartRef, (snapshot) => {
      const items = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setCartItems(items);
      setLoading(false);
    }, (err) => {
      console.error("Errore nel recupero del carrello:", err);
      setError("Impossibile caricare il carrello.");
      setLoading(false);
    });

    // Pulisce il listener quando il componente si smonta o l'utente cambia
    return () => unsubscribe();
  }, [currentUser]); // Dipende dall'utente corrente

  // Funzione per aggiungere un prodotto al carrello in Firestore
  const addToCart = async (product, quantity) => {
    if (!currentUser) {
      setToast({ show: true, message: "Devi essere loggato per aggiungere prodotti al carrello.", variant: 'danger' });
      return;
    }

    const itemRef = doc(db, 'users', currentUser.uid, 'cart', product.id);

    try {
      // Controlla se l'articolo esiste già nel carrello locale per evitare letture inutili da Firestore
      const existingItem = cartItems.find(item => item.id === product.id);

      if (existingItem) {
        // Se esiste, aggiorna la quantità
        await updateDoc(itemRef, {
          quantity: existingItem.quantity + quantity
        });
      } else {
        // Altrimenti, aggiungi il nuovo articolo
        await setDoc(itemRef, {
          ...product, // Salva i dettagli del prodotto
          quantity: quantity,
          shopId: product.shopId // Assicurati che l'ID del negozio sia salvato
        });
      }
    } catch (e) {
      console.error("Errore nell'aggiunta del prodotto al carrello:", e);
      // Potresti voler impostare uno stato di errore o mostrare un messaggio all'utente
    }
  };

  // Funzione per rimuovere un prodotto dal carrello in Firestore
  const removeFromCart = async (productId) => {
    if (!currentUser) {
      return; // Nessun utente autenticato
    }

    const itemRef = doc(db, 'users', currentUser.uid, 'cart', productId);

    try {
      await deleteDoc(itemRef);
    } catch (e) {
      console.error("Errore nella rimozione del prodotto dal carrello:", e);
      // Gestisci l'errore
    }
  };

  // Funzione per aggiornare la quantità di un prodotto nel carrello in Firestore
  const updateItemQuantity = async (productId, quantity) => {
    if (!currentUser || quantity <= 0) {
      // Rimuovi l'articolo se la quantità è 0 o meno
      if (currentUser && quantity <= 0) {
        removeFromCart(productId);
      }
      return; // Nessun utente o quantità non valida
    }

    const itemRef = doc(db, 'users', currentUser.uid, 'cart', productId);

    try {
      await updateDoc(itemRef, {
        quantity: quantity
      });
    } catch (e) {
      console.error("Errore nell'aggiornamento quantità del prodotto:", e);
      // Gestisci l'error
    }
  };

  const updateItemAvailability = (itemId, isAvailable) => {
    setCartItems(prevItems => 
      prevItems.map(item => 
        item.id === itemId 
          ? { ...item, isAvailable } 
          : item
      )
    );
  };

  // Funzione per pulire il carrello
  const clearCart = async () => {
    if (!currentUser) return;

    try {
      const userCartRef = collection(db, 'users', currentUser.uid, 'cart');
      const snapshot = await getDocs(userCartRef);
      
      // Elimina tutti gli items del carrello
      const deletePromises = snapshot.docs.map(doc => 
        deleteDoc(doc.ref)
      );
      
      await Promise.all(deletePromises);
      setCartItems([]);
    } catch (error) {
      console.error("Errore nella pulizia del carrello:", error);
    }
  };

  // TODO: Salvare il carrello in local storage ogni volta che cambia
  // useEffect(() => {
  //   localStorage.setItem('cart', JSON.stringify(cartItems));
  // }, [cartItems]);

  const value = {
    cartItems,
    loading,
    error,
    addToCart,
    removeFromCart,
    updateItemQuantity,
    updateItemAvailability,
    clearCart,
    showToast: (message, variant = 'danger') => setToast({ show: true, message, variant })
  };

  return (
    <CartContext.Provider value={value}>
      {children}
      <Toast
        show={toast.show}
        onClose={() => setToast(prev => ({ ...prev, show: false }))}
        message={toast.message}
        variant={toast.variant}
      />
    </CartContext.Provider>
  );
}

export default CartContext; 